import { Controller } from "@hotwired/stimulus";

// 4-step wizard for creating a contact, opened either from the contacts
// index toolbar (no entreprise pre-filled) or from an entreprise show page
// (entreprise pre-filled and locked). Mirrors the look-and-feel of the
// entreprise-link wizard for consistency.
//
//   0. Identité      — prénom + nom (debounced duplicate scan)
//   1. Entreprise    — existing (Select2) OR new (mode cards with secteur /
//                      activité dropdowns and verify-by-name)
//   2. Qualification — poste + type_contact + état + provenance + premier
//   3. Coordonnées   — email (debounced duplicate scan) + tel + linkedin
//                      + récapitulatif inline
//
// Submission posts to /juniors/:id/contacts ; if the user picked the "new"
// entreprise mode, the form ships `contact[new_entreprise][...]` and the
// server handles the create-and-link in a single transaction.
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent",
    "prevBtn", "nextBtn", "submitBtn", "error",
    // Step 0 — Identité
    "prenomField", "nomField", "contactDup", "contactDupTitle", "contactDupList",
    // Step 1 — Entreprise
    "modeCard", "existingSelect", "newNomField", "entrepriseDup",
    "entrepriseDupTitle", "entrepriseDupList",
    // Step 3 — Coordonnées
    "emailField",
    // Récap
    "recap", "recapName", "recapEntreprise", "recapPoste", "recapEmail",
  ];

  static values = {
    current: { type: Number, default: 0 },
    mode:    { type: String, default: "existing" },
    juniorId: Number,
    contactVerifyUrl:    String,
    entrepriseVerifyUrl: String,
    entrepriseLocked:    { type: Boolean, default: false },
  };

  connect() {
    this._render();
    this._abortByKey = {};
    this._timerByKey = {};
    if (this.hasExistingSelectTarget) {
      this.existingSelectTarget.addEventListener("change", () => this._refreshRecap());
    }
  }

  disconnect() {
    Object.values(this._abortByKey || {}).forEach((a) => a?.abort());
    Object.values(this._timerByKey || {}).forEach((t) => t && clearTimeout(t));
  }

  // ── Navigation ───────────────────────────────────────────────────────

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= 3) return;
    this.currentValue += 1;
    this._render();
    if (this.currentValue === 3) this._refreshRecap();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  switchMode(event) {
    if (this.entrepriseLockedValue) return;
    const mode = event.currentTarget.value;
    this.modeValue = mode;
    this.modeCardTargets.forEach((card) => {
      card.classList.toggle("csm_mode-card--active", card.dataset.mode === mode);
    });
    this._render();
  }

  // ── Duplicate detection ─────────────────────────────────────────────

  checkContactDup() {
    if (this._timerByKey.contact) clearTimeout(this._timerByKey.contact);
    const prenom = this.prenomFieldTarget.value.trim();
    const nom    = this.nomFieldTarget.value.trim();
    if (prenom.length < 2 || nom.length < 2) {
      this._hideDup("contactDup");
      return;
    }
    this._timerByKey.contact = setTimeout(() => {
      this._fetchDup({
        key: "contact",
        url: `${this.contactVerifyUrlValue}?prenom=${encodeURIComponent(prenom)}&nom=${encodeURIComponent(nom)}`,
        target: this.contactDupTarget,
        list:   this.contactDupListTarget,
        title:  this.contactDupTitleTarget,
        renderer: this._renderContactMatches.bind(this),
      });
    }, 350);
  }

  checkEmailDup() {
    if (this._timerByKey.email) clearTimeout(this._timerByKey.email);
    const email = this.emailFieldTarget.value.trim();
    if (email.length < 4 || !email.includes("@")) {
      this._hideDup("contactDup");
      return;
    }
    this._timerByKey.email = setTimeout(() => {
      this._fetchDup({
        key: "email",
        url: `${this.contactVerifyUrlValue}?email=${encodeURIComponent(email)}`,
        target: this.contactDupTarget,
        list:   this.contactDupListTarget,
        title:  this.contactDupTitleTarget,
        renderer: this._renderContactMatches.bind(this),
      });
    }, 350);
  }

  checkEntrepriseDup() {
    if (this._timerByKey.entreprise) clearTimeout(this._timerByKey.entreprise);
    const q = this.newNomFieldTarget.value.trim();
    if (q.length < 3) { this._hideDup("entrepriseDup"); return; }
    this._timerByKey.entreprise = setTimeout(() => {
      this._fetchDup({
        key: "entreprise",
        url: `${this.entrepriseVerifyUrlValue}?q=${encodeURIComponent(q)}`,
        target: this.entrepriseDupTarget,
        list:   this.entrepriseDupListTarget,
        title:  this.entrepriseDupTitleTarget,
        renderer: this._renderEntrepriseMatches.bind(this),
      });
    }, 350);
  }

  _fetchDup({ key, url, target, list, title, renderer }) {
    if (this._abortByKey[key]) this._abortByKey[key].abort();
    this._abortByKey[key] = new AbortController();
    fetch(url, { headers: { Accept: "application/json" }, signal: this._abortByKey[key].signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => renderer(payload, target, list, title))
      .catch(() => {});
  }

  _renderContactMatches(payload, target, list, title) {
    const matches = (payload && payload.matches) || [];
    if (matches.length === 0) { target.hidden = true; list.innerHTML = ""; return; }
    title.textContent = matches.length === 1
      ? "Un contact très proche existe déjà :"
      : `${matches.length} contacts très proches existent déjà :`;
    list.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.poste, m.entreprise].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.prenom || "")} ${this._esc(m.nom || "")}</span>
        ${m.email ? `<span class="csm_dup__meta">${this._esc(m.email)}</span>` : ""}
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
        <a href="${this._esc(m.url)}" target="_blank" class="csm_dup__use" rel="noopener">
          <i class="fa-solid fa-arrow-up-right-from-square"></i> Voir
        </a>
      `;
      list.appendChild(li);
    });
    target.hidden = false;
  }

  _renderEntrepriseMatches(payload, target, list, title) {
    const matches = Array.isArray(payload) ? payload : [];
    if (matches.length === 0) { target.hidden = true; list.innerHTML = ""; return; }
    title.textContent = matches.length === 1
      ? "Une entreprise au nom proche existe déjà :"
      : `${matches.length} entreprises au nom proche existent déjà :`;
    list.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.type_entreprise, m.secteur, m.ville].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.nom)}</span>
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
        <button type="button" class="csm_dup__use" data-id="${m.id}" data-nom="${this._esc(m.nom)}">
          <i class="fa-solid fa-arrow-right"></i> Utiliser celle-ci
        </button>
      `;
      li.querySelector(".csm_dup__use").addEventListener("click", () => {
        this._switchToExistingEntreprise(m.id, m.nom);
      });
      list.appendChild(li);
    });
    target.hidden = false;
  }

  _hideDup(name) {
    if (name === "contactDup" && this.hasContactDupTarget) {
      this.contactDupTarget.hidden = true;
      this.contactDupListTarget.innerHTML = "";
    }
    if (name === "entrepriseDup" && this.hasEntrepriseDupTarget) {
      this.entrepriseDupTarget.hidden = true;
      this.entrepriseDupListTarget.innerHTML = "";
    }
  }

  _switchToExistingEntreprise(id, nom) {
    if (this.entrepriseLockedValue) return;
    const radio = this.modeCardTargets.find((c) => c.dataset.mode === "existing")
                    ?.querySelector("input[type=radio]");
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (this.hasExistingSelectTarget) {
      const $ = window.jQuery || window.$;
      const sel = this.existingSelectTarget;
      if (!Array.from(sel.options).some((o) => String(o.value) === String(id))) {
        sel.appendChild(new Option(nom, id, true, true));
      }
      sel.value = id;
      if ($ && $.fn.select2) $(sel).trigger("change");
      else sel.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this._hideDup("entrepriseDup");
  }

  // ── Validation ──────────────────────────────────────────────────────

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      const prenom = this.prenomFieldTarget.value.trim();
      const nom    = this.nomFieldTarget.value.trim();
      if (!prenom) { this._showError("Le prénom est requis."); this.prenomFieldTarget.focus(); return false; }
      if (!nom)    { this._showError("Le nom est requis.");    this.nomFieldTarget.focus(); return false; }
      return true;
    }
    if (this.currentValue === 1) {
      if (this.entrepriseLockedValue) return true;
      if (this.modeValue === "existing") {
        if (!this.existingSelectTarget.value) {
          this._showError("Choisis une entreprise dans la liste.");
          return false;
        }
        return true;
      }
      if (!this.newNomFieldTarget.value.trim()) {
        this._showError("Le nom de la nouvelle entreprise est requis.");
        this.newNomFieldTarget.focus();
        return false;
      }
      return true;
    }
    return true;
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _render() {
    this.stepTargets.forEach((s) => {
      const stepIdx = Number(s.dataset.step);
      const isCurrent = stepIdx === this.currentValue;
      const matchingMode = !s.dataset.mode || s.dataset.mode === this.modeValue;
      const active = isCurrent && matchingMode;
      s.classList.toggle("esm_wizard__step--active", active);
      if (stepIdx === 1) {
        s.hidden = !isCurrent || !matchingMode;
      } else {
        s.hidden = !isCurrent;
      }
    });

    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;

    this.prevBtnTarget.hidden = this.currentValue === 0;
    const isLast = this.currentValue === 3;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;
  }

  _refreshRecap() {
    if (!this.hasRecapTarget) return;
    if (this.hasRecapNameTarget) {
      const prenom = this.prenomFieldTarget.value.trim();
      const nom    = this.nomFieldTarget.value.trim();
      this.recapNameTarget.textContent = `${prenom} ${nom}`.trim() || "—";
    }
    if (this.hasRecapEntrepriseTarget) {
      let label = "—";
      if (this.entrepriseLockedValue && this.hasExistingSelectTarget) {
        const opt = this.existingSelectTarget.selectedOptions[0];
        if (opt) label = opt.text;
      } else if (this.modeValue === "existing" && this.hasExistingSelectTarget) {
        const opt = this.existingSelectTarget.selectedOptions[0];
        if (opt) label = opt.text;
      } else if (this.modeValue === "new" && this.hasNewNomFieldTarget) {
        label = this.newNomFieldTarget.value.trim() || "Nouvelle entreprise";
      }
      this.recapEntrepriseTarget.textContent = label;
    }
    if (this.hasRecapEmailTarget && this.hasEmailFieldTarget) {
      this.recapEmailTarget.textContent = this.emailFieldTarget.value.trim() || "—";
    }
    if (this.hasRecapPosteTarget) {
      const posteEl = this.element.querySelector('[name="contact[poste]"]');
      this.recapPosteTarget.textContent = (posteEl?.value || "").trim() || "—";
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _showError(msg) {
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
